import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Clock, User, Linkedin, Twitter, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import DOMPurify from 'dompurify';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const BlogPost = ({ postId, onBack, onBackToHome }) => {
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(apiUrl(`blog/posts/${postId}`));
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        fetchRelatedPosts(data.category);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      logger.error('Error fetching post:', err);
      setError('Failed to load post');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedPosts = async (category) => {
    try {
      const response = await fetch(apiUrl(`blog/posts?category=${encodeURIComponent(category)}`));
      if (response.ok) {
        const data = await response.json();
        setRelatedPosts(data.filter(p => p.id !== postId).slice(0, 2));
      }
    } catch (err) {
      logger.error('Error fetching related posts:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Post not found'}</h2>
          <Button onClick={onBack}>Back to Blog</Button>
        </div>
      </div>
    );
  }

  // Build the canonical share URL for this blog post.
  // We point social platforms at the backend OG endpoint so crawlers
  // (LinkedIn, Twitter, Facebook, WhatsApp) can read rich preview meta
  // tags from server-rendered HTML. Real users are redirected to /blog/<id>.
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${origin}/api/v1/blog/og/${post.id}`;
  const shareTitle = post.title || '';

  const openShare = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const shareOnLinkedIn = () => {
    openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
  };

  const shareOnTwitter = () => {
    openShare(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={onBack}
              className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </button>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Share:</span>
              <button
                onClick={shareOnLinkedIn}
                aria-label="Share on LinkedIn"
                title="Share on LinkedIn"
                data-testid="blog-share-linkedin"
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </button>
              <button
                onClick={shareOnTwitter}
                aria-label="Share on Twitter"
                title="Share on Twitter"
                data-testid="blog-share-twitter"
                className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-blue-400 transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[400px] overflow-hidden">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full mb-4 inline-block">
              {post.category}
            </span>
            <h1 className="text-3xl lg:text-4xl font-bold text-white max-w-4xl">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-gray-200 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-700">
                {post.author.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <div className="font-semibold text-gray-900">{post.author}</div>
                <div className="text-sm text-gray-500">{post.authorRole}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </div>

          {/* Article Content */}
          <article 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-blue-600 prose-strong:text-gray-900 prose-ul:text-gray-600 prose-ol:text-gray-600 prose-li:marker:text-blue-600 prose-ol:list-decimal prose-ul:list-disc"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
          />

          {/* CTA Box */}
          <div className="mt-12 p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Ready to see CoachMeUpp in action?
            </h3>
            <p className="text-gray-600 mb-6">
              Discover how our platform can help your organization achieve measurable coaching outcomes.
            </p>
            <Button 
              onClick={() => {
                onBackToHome();
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Request a Demo
            </Button>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
              {relatedPosts.map((relatedPost) => (
                <article 
                  key={relatedPost.id}
                  onClick={() => {
                    window.scrollTo({ top: 0 });
                    // Trigger re-fetch by updating postId
                  }}
                  className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-sm text-blue-600 font-medium">{relatedPost.category}</span>
                    <h3 className="text-lg font-bold text-gray-900 mt-2 group-hover:text-blue-600 transition-colors">
                      {relatedPost.title}
                    </h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="bg-gray-900 py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">Want to explore more insights?</p>
          <Button 
            onClick={onBack}
            variant="outline"
            className="border-white text-white hover:bg-white hover:text-gray-900"
          >
            View All Articles
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
